import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { token } from '@atlaskit/tokens';
import { Field, FormHeader, ErrorMessage } from '@atlaskit/form';
import Select from '@atlaskit/select';
import TaxIdInput from './MACContactDetailsTaxIdInput';
import { Country } from 'modules/data/countries/model';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { styled } from 'styled-components';
import { Dimensions } from 'components/visuals';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { State } from 'model/State';
import { getTaxRate } from 'modules/data/taxRates/selectors';
import SectionMessage from '@atlaskit/section-message';
import config from 'appconfig';
import t from 'modules/i18n/intl';
import { SelectOption } from '../UpdatePaymentDetailsForm';
import {
  selectAddressValidationResult,
  selectTaxIdValidationResult,
  selectValidateCountryChangeProgress,
} from '../../duck/selectors';
import Spinner from '@atlaskit/spinner';
import ContactDetailField from './ContactDetailField';

const SelectWrapper = styled.div`
  width: ${Dimensions.units(50)}px;
`;

const CurrencyChargedIn = styled.div`
  font-size: 10pt;
  color: ${token('color.icon.subtle', '#626F86')};
`;

const ReconcilableMessageSection = styled.div`
  margin: ${Dimensions.units(2)}px 0;
`;

const filedLengthLimits = {
  address1: 50,
  address2: 100,
  city: 50,
  postcode: 11,
};

const WarningIconComponent = () => (
  <WarningIcon label="Warning icon" color={token('color.icon.danger', '#E34935')} />
);

export type BillingDetails = {
  firstName: string;
  lastName: string;
  phone?: string;
  companyName: string;
  address1: string;
  address2: string;
  city: string;
  country: SelectOption;
  state: SelectOption;
  postcode: string;
  taxId: string;
  vatResponsible: boolean | null;
  vatStatus?: 'CORPORATE' | 'INDIVIDUAL' | null;
  taxExemptions: string;
};

type DetailsFormProps = {
  countryDetails: Country;
  billingDetailsForm: BillingDetails;
  states: SelectOption[];
  countryOptions: SelectOption[];
  currencyChangeCountry: boolean;
  currencyChangeCountryMessageDetails: {
    defaultMessage: string;
    id: string;
    values: any;
  };
  hasReconcilableWarning?: boolean;
  handleFormChange: (e, field: string) => void;
  handleFormStateSelectChange: (e) => void;
  handleFormCountrySelectChange: (e) => void;
  setBillingDetailsForm: Dispatch<SetStateAction<BillingDetails>>;
  isEditDetailsPage?: boolean;
  showUpdateOrgDetailsWarning?: boolean;
  orgContactsToDisplay?: any[];
};

const contactUs = (chunks) => (
  <a href={config.companyContact} target="_blank" rel="noreferrer">
    {chunks}
  </a>
);

export const ContactDetailsFormSection = ({
  countryDetails,
  billingDetailsForm,
  states,
  countryOptions,
  currencyChangeCountry,
  currencyChangeCountryMessageDetails,
  hasReconcilableWarning,
  handleFormChange,
  handleFormStateSelectChange,
  handleFormCountrySelectChange,
  setBillingDetailsForm,
  isEditDetailsPage,
  showUpdateOrgDetailsWarning,
  orgContactsToDisplay,
}: DetailsFormProps) => {
  const taxRateForIsoAndState = useSelector((state: State) =>
    getTaxRate(state, {
      isoCountryCode: billingDetailsForm.country.value,
      stateKey: billingDetailsForm.state.value,
    }),
  ).ifSuccess((r) => r);
  const validateCountryChangeProgress = useSelector((state: State) =>
    selectValidateCountryChangeProgress(state, billingDetailsForm.country.value),
  );

  const [taxIdLabel, setTaxIdLabel] = useState('');
  const taxIdValidationResult = useSelector((state) =>
    selectTaxIdValidationResult(state as State, billingDetailsForm.taxId),
  );
  const addressValidationResult = useSelector((state) =>
    selectAddressValidationResult(
      state as State,
      billingDetailsForm?.address1 + billingDetailsForm?.address2,
    ),
  );
  const addressValidationErrorMessage =
    addressValidationResult?.failed || addressValidationResult?._result?.failureEntity
      ? addressValidationResult?._result?.failureEntity?.errorDetail ||
        t({
          id: 'mac.edit-details.invalid.address',
          defaultMessage: 'Address is not valid',
        })
      : '';

  useEffect(() => {
    if (taxRateForIsoAndState) {
      setTaxIdLabel(
        taxRateForIsoAndState.taxIdLabel === 'ABN'
          ? 'Australian Business Number (ABN)'
          : taxRateForIsoAndState.taxIdLabel,
      );
    }
  }, [taxRateForIsoAndState]);

  return (
    <>
      <ContactDetailField
        isRequired
        fieldName="firstName"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.first-name-input-label"
            defaultMessage="First Name"
          />
        }
        value={billingDetailsForm.firstName}
        changeHandler={(e) => handleFormChange(e, 'firstName')}
        dataCy="mac-billing-details-first-name-input"
        width={Dimensions.units(50)}
      />
      <ContactDetailField
        isRequired
        fieldName="lastName"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.last-name-input-label"
            defaultMessage="Last Name"
          />
        }
        value={billingDetailsForm.lastName}
        changeHandler={(e) => handleFormChange(e, 'lastName')}
        dataCy="mac-billing-details-last-name-input"
        width={Dimensions.units(50)}
      />
      {isEditDetailsPage && (
        <>
          <ContactDetailField
            fieldName="phone"
            fieldLabel={
              <FormattedMessage
                id="mac.payment-details.billing-form.phone-input-label"
                defaultMessage="Phone"
              />
            }
            value={billingDetailsForm.phone}
            changeHandler={(e) => handleFormChange(e, 'phone')}
            dataCy="mac-billing-details-phone-input"
            width={Dimensions.units(50)}
          />
          <br />
          <FormHeader title={t('mac.edit-details.org.contact.title')} />
          {showUpdateOrgDetailsWarning && orgContactsToDisplay && (
            <>
              {t(
                {
                  id: 'mac.edit-details.org.contact.warning',
                  defaultMessage:
                    'Updating your organization information below will also update the organization information for all associated users. Please <contactUs>contact us</contactUs> if your organization information is associated incorrectly.',
                },
                {
                  contactUs,
                },
              )}
              <ul>
                {orgContactsToDisplay.map((contact) => (
                  <li key={`${contact.contactDetails.displayName}-${contact.contactDetails.email}`}>
                    {contact.contactDetails.displayName} {contact.contactDetails.email}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      <br />
      <ContactDetailField
        isRequired
        fieldName="companyName"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.company-name-input-label"
            defaultMessage="Company Name"
          />
        }
        value={billingDetailsForm.companyName}
        changeHandler={(e) => handleFormChange(e, 'companyName')}
        dataCy="mac-billing-details-company-name-input"
        width={Dimensions.units(50)}
      />
      <ContactDetailField
        isRequired
        fieldName="address1"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.address-1-input-label"
            defaultMessage="Address 1"
          />
        }
        value={billingDetailsForm.address1}
        changeHandler={(e) => handleFormChange(e, 'address1')}
        dataCy="mac-billing-details-address-1-input"
        width={Dimensions.units(50)}
        lengthLimit={filedLengthLimits.address1}
        errorMessage={addressValidationErrorMessage}
      />
      <ContactDetailField
        fieldName="address2"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.address-2-input-label"
            defaultMessage="Address 2"
          />
        }
        value={billingDetailsForm.address2}
        changeHandler={(e) => handleFormChange(e, 'address2')}
        dataCy="mac-billing-details-address-2-input"
        width={Dimensions.units(50)}
        lengthLimit={filedLengthLimits.address2}
        errorMessage={addressValidationErrorMessage}
      />
      <ContactDetailField
        isRequired
        fieldName="city"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.city-input-label"
            defaultMessage="City"
          />
        }
        value={billingDetailsForm.city}
        changeHandler={(e) => handleFormChange(e, 'city')}
        dataCy="mac-billing-details-city-input"
        width={Dimensions.units(50)}
        lengthLimit={filedLengthLimits.city}
      />
      <ContactDetailField
        fieldName="postcode"
        fieldLabel={
          <FormattedMessage
            id="mac.payment-details.billing-form.postcode-input-label"
            defaultMessage="Postcode/ZIP"
          />
        }
        value={billingDetailsForm.postcode}
        changeHandler={(e) => handleFormChange(e, 'postcode')}
        dataCy="mac-billing-details-postcode-input"
        width={Dimensions.units(50)}
        lengthLimit={filedLengthLimits.postcode}
      />
      <Field
        aria-required
        isRequired
        name="country"
        id="country"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.country-input-label"
            defaultMessage="Country"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <SelectWrapper data-cy="mac-billing-details-country-input">
              <Select
                {...fieldProps}
                hasAutocomplete
                options={countryOptions}
                onChange={handleFormCountrySelectChange}
                value={billingDetailsForm.country || ''}
              />
            </SelectWrapper>
            {!billingDetailsForm.country.value && (
              <ErrorMessage>
                <FormattedMessage
                  defaultMessage="This field is required. It can not be empty"
                  id="mac.payment-details.billing-form.field-empty"
                />
              </ErrorMessage>
            )}
          </>
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="state"
        id="state"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.state-input-label"
            defaultMessage="State/Province"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <SelectWrapper data-cy="mac-billing-details-state-input">
              <Select
                {...fieldProps}
                hasAutocomplete
                options={states}
                onChange={handleFormStateSelectChange}
                value={billingDetailsForm.state || ''}
              />
            </SelectWrapper>
            {!billingDetailsForm.state.value && (
              <ErrorMessage>
                <FormattedMessage
                  defaultMessage="This field is required. It can not be empty"
                  id="mac.payment-details.billing-form.field-empty"
                />
              </ErrorMessage>
            )}
          </>
        )}
      </Field>
      <SelectWrapper>
        <TaxIdInput
          taxRate={taxRateForIsoAndState}
          showTaxId
          taxIdLabel={taxIdLabel}
          country={countryDetails as unknown as Country}
          vatResponsible={billingDetailsForm.vatResponsible}
          vatStatus={billingDetailsForm.vatStatus}
          taxId={billingDetailsForm.taxId}
          taxExemptions={billingDetailsForm.taxExemptions}
          handleFormChange={handleFormChange}
          setBillingDetailsForm={setBillingDetailsForm}
          taxIdValidationResult={taxIdValidationResult}
          state={billingDetailsForm.state.value}
        />
      </SelectWrapper>
      <ReconcilableMessageSection>
        {validateCountryChangeProgress?.inProgress ? (
          <Spinner size="medium" />
        ) : (
          <>
            {currencyChangeCountry && (
              <SectionMessage appearance="error" icon={WarningIconComponent}>
                <FormattedMessage {...currencyChangeCountryMessageDetails} />
              </SectionMessage>
            )}
            {hasReconcilableWarning && (
              <CurrencyChargedIn>
                <FormattedMessage
                  defaultMessage="You will be charged in {currencyCode}. All future Atlassian orders related to the end user will change to {currencyCode}."
                  id="mac.edit-details.org.contact.reconcilable.warning"
                  values={{
                    currencyCode: countryDetails.primaryCurrency || '',
                  }}
                />
              </CurrencyChargedIn>
            )}
          </>
        )}
      </ReconcilableMessageSection>
    </>
  );
};
